import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Loader2, CheckCircle2, XCircle, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';

const SCOPES = [
  'ZohoInvoice.invoices.CREATE',
  'ZohoInvoice.invoices.READ',
  'ZohoInvoice.invoices.UPDATE',
  'ZohoInvoice.contacts.CREATE',
  'ZohoInvoice.contacts.READ',
  'ZohoInvoice.contacts.UPDATE',
  'ZohoInvoice.customerpayments.CREATE',
  'ZohoInvoice.customerpayments.READ',
  'ZohoInvoice.settings.READ',
];

const SCOPES_CSV = SCOPES.join(',');

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ok ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30' : 'bg-red-500/10 text-red-600 border border-red-500/30'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label}
  </span>
);

export const ZohoAuthModal = ({ open, onClose, api }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [exchanging, setExchanging] = useState(false);
  const [newRefreshToken, setNewRefreshToken] = useState('');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/zoho-admin/status');
      setStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch Zoho status');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) {
      setNewRefreshToken('');
      setCode('');
      fetchStatus();
    }
  }, [open, fetchStatus]);

  const copy = (val) => {
    navigator.clipboard.writeText(val);
    toast.success('Copied');
  };

  const exchangeCode = async () => {
    if (!code.trim()) { toast.error('Paste the authorization code first'); return; }
    setExchanging(true);
    try {
      const res = await api.post('/zoho-admin/exchange-code', { code: code.trim() });
      setNewRefreshToken(res.data.refresh_token);
      toast.success('Refresh token generated. Copy it into backend/.env and restart backend.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Exchange failed');
      const zoho = detail?.zoho_response;
      toast.error(zoho ? `${msg} — ${JSON.stringify(zoho)}` : msg);
    } finally {
      setExchanging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="zoho-auth-modal">
        <DialogHeader>
          <DialogTitle>Zoho Invoice — OAuth Token Health</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Status */}
          <div className="p-4 rounded-lg border border-border bg-card/40">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Current Configuration</p>
              <Button size="sm" variant="ghost" onClick={fetchStatus} disabled={loading} data-testid="zoho-status-refresh">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            {!status ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : (
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge ok={!!status.configured} label="Env configured" />
                  <StatusBadge ok={!!status.refresh_ok} label="Refresh token valid" />
                  <StatusBadge ok={!!status.has_customerpayments_scope} label="customerpayments scope" />
                </div>
                {status.org_id && (
                  <p className="text-muted-foreground">Org ID: <span className="font-mono">{status.org_id}</span> · Client: <span className="font-mono">{status.client_id_prefix}…</span></p>
                )}
                {!status.has_customerpayments_scope && status.scope_detail && (
                  <details className="text-muted-foreground">
                    <summary className="cursor-pointer">Zoho response (why scope check failed)</summary>
                    <pre className="mt-2 p-2 rounded bg-muted/50 overflow-x-auto text-[10px]">{JSON.stringify(status.scope_detail, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          {status && !status.has_customerpayments_scope && (
            <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 space-y-3">
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Regenerate the refresh token</p>
              <ol className="text-xs space-y-2 list-decimal list-inside text-muted-foreground">
                <li>
                  Open{' '}
                  <a href="https://api-console.zoho.in/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                    api-console.zoho.in <ExternalLink className="w-3 h-3" />
                  </a>{' '}
                  and pick your <b>Self-Client</b> for Clayworks.
                </li>
                <li>Click <b>Generate Code</b>. Paste the scope list below (Time Duration = 10 min):</li>
              </ol>
              <div className="flex items-center gap-2">
                <Input readOnly value={SCOPES_CSV} className="text-xs font-mono" data-testid="zoho-scopes" />
                <Button size="sm" variant="outline" onClick={() => copy(SCOPES_CSV)} data-testid="copy-scopes">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <ol className="text-xs space-y-2 list-decimal list-inside text-muted-foreground" start={3}>
                <li>Copy the authorization code Zoho gives you and paste it below (do this within 10 minutes — codes expire fast).</li>
                <li>We&apos;ll exchange it for a new refresh token. Copy that into <span className="font-mono">/app/backend/.env</span> → <span className="font-mono">ZOHO_REFRESH_TOKEN</span>, then restart the backend.</li>
              </ol>
            </div>
          )}

          {/* Exchange code */}
          <div className="p-4 rounded-lg border border-border bg-card/40 space-y-2">
            <Label className="text-sm font-semibold">Exchange Authorization Code</Label>
            <Textarea
              placeholder="Paste Zoho authorization code here (e.g. 1000.abc123…def456)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              rows={2}
              className="text-xs font-mono"
              data-testid="zoho-auth-code"
            />
            <Button onClick={exchangeCode} disabled={exchanging || !code.trim()} className="w-full" data-testid="exchange-code-btn">
              {exchanging ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exchanging…</> : 'Exchange for Refresh Token'}
            </Button>
          </div>

          {/* Result */}
          {newRefreshToken && (
            <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 space-y-2">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">New refresh token generated</p>
              <p className="text-xs text-muted-foreground">Copy this into <span className="font-mono">ZOHO_REFRESH_TOKEN</span> in <span className="font-mono">/app/backend/.env</span> (keep the quotes), then run <span className="font-mono">sudo supervisorctl restart backend</span> and click Refresh above to verify.</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={newRefreshToken} className="text-xs font-mono" data-testid="new-refresh-token" />
                <Button size="sm" variant="outline" onClick={() => copy(newRefreshToken)} data-testid="copy-refresh-token">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="zoho-modal-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
